import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import RadioButtons from '../../components/RadioButtons';
import { SUBMIT_TEST_TAKEN } from '../../constants/heapEvents';
import { MAX_TEST_SCORE } from '../../constants/numberLimit';
import analytics from '../../lib/analytics';
import { track } from '../../lib/heapHelpers';
import { isInteger } from '../../lib/helpers';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { availableTests } from '../../lib/testValidationHelper';
import { validateTestScore } from '../../lib/testValidationHelper';
import './index.scss';
import { updateValue } from '../../utils/utils';

const TestTakenPage = ({ testType, initialTestScore, dispatchAddPageData, dispatchWizardNav }) => {
  const [selectedTestType, setTestType] = useState(testType);
  const [testScore, setTestScore] = useState(initialTestScore);
  const [error, setError] = useState(null);
  const radioOptions = availableTests();

  const [debouncedError, setDebouncedError] = useState(null);

  useEffect(() => {
    // Set a timeout to update the debounced error after 500ms
    const timerId = setTimeout(() => {
      setDebouncedError(error);
    }, 500);

    // Clear the timeout if the error changes before the 500ms are up
    return () => {
      clearTimeout(timerId);
    };
  }, [error]);

  const isInvalid = useMemo(() => {
    if (selectedTestType === '') {
      setError(null);
      return true;
    }

    if (selectedTestType !== '' && testScore === '') {
      setError(null);
      return true;
    }
    if (selectedTestType !== '' && !isInteger(testScore)) {
      setError(null);
      return true;
    }

    const errorMessage = validateTestScore(selectedTestType, parseInt(testScore, 10));
    setError(errorMessage);

    return errorMessage !== null;
  }, [setError, testScore, selectedTestType]);

  const renderError = () => {
    if (isInvalid && debouncedError !== null) {
      return <div className="row-spacer text-error">{debouncedError}</div>;
    }

    return null;
  };

  const changeTestType = event => {
    const value = event.target.value;
    if (value === selectedTestType) {
      setTestType('');
      setTestScore('');
    } else {
      setTestType(value);
      setTestScore('');
    }
  };

  const submitAndContinue = (testType, testScore) => {
    const testTypePayload = payloadFactory.createAddPageDataPayload('testType', testType);
    const testScorePayload = payloadFactory.createAddPageDataPayload('testScore', testScore);
    dispatchAddPageData(testTypePayload);
    dispatchAddPageData(testScorePayload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const handleApplyDefault = event => {
    event.preventDefault();
    submitAndContinue('', '');
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'test-taken');
    track(SUBMIT_TEST_TAKEN, { 'Test type': selectedTestType, 'Test score': testScore });
    submitAndContinue(selectedTestType, testScore);
  };

  return (
    <PageLayout title="Have you taken any tests in the past 6 months?">
      <div className="row-spacer">
        <div className="row-spacer margin-bottom">
          <p>If you have taken the same test multiple times, you can enter your highest score.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row-spacer">
            <div className="test-type-container">
              <RadioButtons
                radioOptions={radioOptions}
                defaultOption={selectedTestType}
                onChange={changeTestType}
              />
            </div>
          </div>
          <div className="row">
            <div className="col col--7/16-large">
              <p
                className={
                  selectedTestType === ''
                    ? 'text-field__input-header--disabled'
                    : 'text-field__input-header'
                }
              >{`${selectedTestType} Score`}</p>
              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="test-score"
                value={testScore}
                onChange={e => updateValue(e, setTestScore, MAX_TEST_SCORE)}
                disabled={selectedTestType === ''}
                min={0}
                max={MAX_TEST_SCORE}
              />
            </div>
          </div>

          <div className="row-spacer">{renderError()}</div>

          <div className="row-spacer margin-bottom">
            <Button type="submit" isDisabled={isInvalid}>
              Next
            </Button>
          </div>

          <div className="row-spacer">
            <Link to="/save-personal-info" onClick={handleApplyDefault}>
              I haven't taken any tests recently
            </Link>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

TestTakenPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  testType: PropTypes.string.isRequired,
  initialTestScore: PropTypes.string.isRequired,
};

export const mapStateToProps = reduxStore => ({
  testType: reduxStore.builder.ui.testType,
  initialTestScore: reduxStore.builder.ui.testScore,
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { TestTakenPage };

export default connect(mapStateToProps, mapDispatchToProps)(TestTakenPage);
